import { isSelfServePlan } from "@/lib/self-serve-plans";

export interface PlanChargeShape {
  payment_strategy?: string | null;
  price_monthly: number;
}

/**
 * How a plan is charged decides which endpoint can take the money: a
 * preapproval plan is a recurring authorization and cannot be paid as a
 * one-shot, and a cash plan has no self-service checkout at all.
 */
export function planPaymentStrategy(plan: PlanChargeShape | null): string {
  if (!plan) return "cash";
  return plan.payment_strategy || (plan.price_monthly > 0 ? "preapproval" : "cash");
}

/** The edge function that can actually charge a plan with this strategy. */
export function reactivationEndpoint(strategy: string): "mercadopago-preapproval-create" | "mercadopago-checkout" {
  return strategy === "preapproval" ? "mercadopago-preapproval-create" : "mercadopago-checkout";
}

export interface UpgradablePlan extends PlanChargeShape {
  id: string;
  is_custom: boolean;
  sort_order: number;
  billing_mode?: string | null;
}

/**
 * A suspended workspace on the standard free tier gets back in by buying a real
 * plan, not by emailing support. Only catalog plans qualify: a negotiated custom
 * plan has no price to offer, and only preapproval plans can actually be charged.
 */
export function upgradeOffersFor<T extends UpgradablePlan>(currentPlan: UpgradablePlan | null, catalog: T[]): T[] {
  if (!currentPlan) return [];
  if (planPaymentStrategy(currentPlan) !== "cash") return [];
  if (currentPlan.is_custom) return [];
  return catalog
    .filter((p) => isSelfServePlan(p) && p.price_monthly > 0 && planPaymentStrategy(p) === "preapproval")
    .sort((a, b) => a.sort_order - b.sort_order);
}
